import api from './api';
import { setAuthFailed } from '../utils/auth0Utils';
import { performLogout } from './logoutService';
import { STORAGE_KEYS } from '../utils/storageKeys';

// Define interface for user profile response from backend
export interface UserProfileResponse {
  user: {
    id: string;
    username: string;
    email: string;
    first_name?: string;
    last_name?: string;
    is_active: boolean;
    company?: string;
    auth0_user_id?: string;
    invitation_sent_at?: string | null;
    invitation_accepted_at?: string | null;
  };
  access_token: string;
  expires_in: number;
  expires_at: number;
  issued_at: number;
  token_type: string;
}

// Token refresh buffer (in milliseconds) - refresh token 5 minutes before expiry
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000;

export type TokenProvider = () => Promise<string>;
let globalTokenProvider: TokenProvider | null = null;

let globalExtendedToken: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

let lastAuthErrorType: string | null = null;
export const getLastAuthErrorType = (): string | null => lastAuthErrorType;

class AppAuthService {
  private static instance: AppAuthService;
  private apiCallPromise: Promise<UserProfileResponse | null> | null = null;
  private apiCallResult: UserProfileResponse | null = null;
  private apiCallInProgress = false;

  private constructor() {}

  public static getInstance(): AppAuthService {
    if (!AppAuthService.instance) {
      AppAuthService.instance = new AppAuthService();
    }
    return AppAuthService.instance;
  }

  public async getUserProfileWithAccessToken(accessToken: string, forceRefresh = false): Promise<UserProfileResponse | null> {
    const extendedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!forceRefresh && this.apiCallResult && extendedToken) {
      return this.apiCallResult;
    }
    if (this.apiCallInProgress && this.apiCallPromise) {
      return this.apiCallPromise;
    }

    this.apiCallInProgress = true;
    this.apiCallResult = null; // Clear cached result on force refresh or new call

    this.apiCallPromise = new Promise<UserProfileResponse | null>((resolve) => {
      api.get<UserProfileResponse>('/users/auth0/me/', {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((response) => {
        this.apiCallResult = response.data;

        // ✅ Set extended token immediately
        setExtendedToken(response.data.access_token);
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.access_token);

        // ✅ Store expiry info & schedule background refresh
        if (response.data.expires_at) {
          // ✅ FIXED: Backend expires_at is typically in seconds, convert to milliseconds
          const expiresAtMs = response.data.expires_at * 1000;
          storeTokenExpiry(expiresAtMs);
          scheduleTokenRefresh();
        } else if (response.data.expires_in) {
          // ✅ Convert expires_in (seconds) to milliseconds timestamp
          const expiresAtMs = Date.now() + (response.data.expires_in * 1000);
          storeTokenExpiry(expiresAtMs);
          scheduleTokenRefresh();
        }

        lastAuthErrorType = null;
        resolve(this.apiCallResult);
      })
      .catch((error: unknown) => {
        console.warn('Auth0 token exchange: API call failed:', error);
        // Don't force logout here - let caller handle retry logic
        this.apiCallResult = null;
        lastAuthErrorType = (error as any)?.response?.data?.error_type ?? null;
        resolve(null);
      })
      .finally(() => {
        this.apiCallInProgress = false;
      });
    });

    return this.apiCallPromise;
  }

  public reset(): void {
    this.apiCallPromise = null;
    this.apiCallResult = null;
    this.apiCallInProgress = false;

    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    globalExtendedToken = null;

    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }
}

// ------------------
// Helper Functions
// ------------------

// Export a function that uses the singleton
export const getUserProfileWithAccessToken = async (accessToken: string, forceRefresh = false): Promise<UserProfileResponse | null> => {
  return AppAuthService.getInstance().getUserProfileWithAccessToken(accessToken, forceRefresh);
};

// Bounded retry tolerance around the refresh/token-exchange call only — a single
// transient network blip on this call should not force an immediate logout.
const REFRESH_RETRY_ATTEMPTS = 2;
const REFRESH_RETRY_DELAY_MS = 500;

export const refreshExtendedToken = async (accessToken: string): Promise<UserProfileResponse | null> => {
  for (let attempt = 1; attempt <= REFRESH_RETRY_ATTEMPTS; attempt++) {
    const result = await getUserProfileWithAccessToken(accessToken, true);
    if (result?.access_token) return result;
    if (attempt < REFRESH_RETRY_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
    }
  }
  return null;
};

export const setTokenProvider = (provider: TokenProvider): void => {
  globalTokenProvider = provider;
};

export const getExtendedToken = (): string | null => globalExtendedToken;

export const setExtendedToken = (token: string): void => {
  globalExtendedToken = token;
};

export const resetAppAuthService = (): void => {
  AppAuthService.getInstance().reset();
  stopTokenRefresh();
};

export const storeTokenExpiry = (expiresAtMs: number): void => {
  // ✅ FIXED: Always store timestamps in milliseconds with clear naming
  localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAtMs.toString());
};

let refreshInterval: ReturnType<typeof setInterval> | null = null;

export const scheduleTokenRefresh = (): void => {
  // clear any previous interval
  if (refreshInterval) clearInterval(refreshInterval);

  refreshInterval = setInterval(async () => {
    const expiresAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!expiresAtStr || !token) return;

    // ✅ FIXED: Parse stored milliseconds timestamp correctly  
    const expiresAtMs = parseInt(expiresAtStr, 10);
    const nowMs = Date.now();

    // ✅ Both values are now guaranteed to be in milliseconds
    if (expiresAtMs - nowMs <= TOKEN_REFRESH_BUFFER) {
      console.info('🔄 Token expiring soon, attempting refresh...');
      try {
        let auth0Token: string | null = null;
        
        // Try to get a fresh Auth0 token from the provider if available
        if (globalTokenProvider) {
          try {
            auth0Token = await globalTokenProvider();
          } catch (providerErr) {
            console.error('❌ Token provider failed:', providerErr);
          }
        }

        // If we don't have a fresh Auth0 token, we can't properly refresh the backend token
        if (!auth0Token) {
          console.warn('⚠️ No Auth0 token available for refresh. Waiting for next API call to handle 401.');
          return;
        }

        const response = await refreshExtendedToken(auth0Token);
        if (response?.access_token) {
          setExtendedToken(response.access_token);
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.access_token);

          api.defaults.headers.common.Authorization = `Bearer ${response.access_token}`;

          console.info('✅ Token refreshed and applied globally');
        } else {
          console.error('❌ Silent refresh returned no token');
          // ✅ FIXED: Use consistent milliseconds variable names
          if (expiresAtMs - nowMs < 60 * 1000) {
            console.error('❌ Token nearly expired and refresh failed, logging out');
            performLogout();
          }
        }
      } catch (err) {
        console.error('❌ Silent refresh failed:', err);
      }
    }
  }, 60 * 1000);
};

export const initializeAppAuthService = (): void => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const expiresAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);

  if (token && expiresAtStr) {
    // ✅ FIXED: Parse milliseconds timestamp with clear variable naming
    const expiresAtMs = parseInt(expiresAtStr, 10);
    const nowMs = Date.now();

    // ✅ Both values are guaranteed to be in milliseconds
    if (expiresAtMs > nowMs) {
      console.info('🔐 Restoring existing token and initializing silent refresh...');
      setExtendedToken(token);
      scheduleTokenRefresh();
    } else {
      console.warn('⚠️ Stored token expired — clearing...');
      resetAppAuthService();
    }
  } else {
    console.info('ℹ️ No stored token found — user likely not logged in yet.');
  }
};


export const stopTokenRefresh = (): void => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

export const hasValidExtendedToken = (): boolean => {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token || !globalExtendedToken) return false;

  try {
    const expiresAtStr = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    if (!expiresAtStr) return true;

    // ✅ FIXED: Parse milliseconds timestamp with consistent naming
    const expiresAtMs = parseInt(expiresAtStr, 10);
    const nowMs = Date.now();
    // ✅ All values are in milliseconds now
    return expiresAtMs > (nowMs + TOKEN_REFRESH_BUFFER);
  } catch (error) {
    console.error('Error checking token validity:', error);
    return false;
  }
};
